import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material';
import { useParams, useLocation } from 'react-router-dom';
import DetailHeader from '../DetailHeader.js';
import RecipeServings from './RecipeServings.js';
import RecipeIngredients from './RecipeIngredients.js';
import RecipeInstructions from './RecipeInstructions.js';
import RecipeImage from './RecipeImage.js';
import RecipeNotes from './RecipeNotes.js';
import MenuDialog from './MenuDialog.js';
import CreateMenuDialog from './CreateMenuDialog.js';
import DeleteRecipeDialog from './DeleteRecipeDialog.js';
import RecipeActions from './RecipeActions.js';
import { useRecipeDetail } from '../../hooks/useRecipeDetail.js';
import { useNavigation } from '../../App.js';

function RecipeDetail({ 
  recipes, 
  inventory, 
  togglePossession, 
  favorites, 
  bookmarks,
  toggleFavorite,
  toggleBookmark,
  menus,
  onAddRecipeToMenu,
  onCreateMenu,
  scrollRef,
  originFilter,
  user,
  onRecipeDeleted,
  defaultMeasurementSystem = 'imperial'
}) {
  const { recipeId } = useParams();
  const theme = useTheme();
  const location = useLocation();
  const { getPreviousPage } = useNavigation();
  
  const { 
    loading,
    recipe,
    servings,
    setServings,
    imageError,
    setImageError,
    isMenuDialogOpen,
    setIsMenuDialogOpen,
    isCreateMenuDialogOpen,
    setIsCreateMenuDialogOpen,
    newMenuName,
    setNewMenuName,
    deleteDialogOpen,
    setDeleteDialogOpen,
    handleCreateAndAddToMenu: originalHandleCreateAndAddToMenu,
    handleKeyPress: originalHandleKeyPress,
    handleDeleteRecipe,
    userRecipe
  } = useRecipeDetail({ 
    recipeId, 
    user, 
    recipes, 
    inventory, 
    scrollRef, 
    onRecipeDeleted,
    defaultMeasurementSystem
  });
  
  const handleCreateAndAddToMenu = () => originalHandleCreateAndAddToMenu(onCreateMenu);
  const handleKeyPress = (event) => originalHandleKeyPress(event, onCreateMenu);

  // Determine back navigation
  const previousPage = getPreviousPage(location.pathname);
  const from = previousPage?.from || location.state?.from;

  let backLink, backLabel;
  if (from && from.type && from.id) {
    if (from.type === 'menu') {
      const menu = menus.find(m => m.id === from.id);
      backLink = `/cocktail-menu/${from.id}`;
      backLabel = menu ? menu.name : 'Cocktail Menu';
    } else if (from.type === 'ingredient') {
      const ingredient = inventory.find(i => i.id === from.id);
      backLink = `/ingredient/${from.id}`;
      backLabel = ingredient ? ingredient.name : 'Ingredient';
    } else if (from.type === 'recipe') {
      const prevRecipe = recipes.find(r => r.id === from.id);
      backLink = `/recipe/${from.id}`;
      backLabel = prevRecipe ? prevRecipe.name : 'Recipe';
    } else {
      backLink = originFilter ? `/recipes/collection/${encodeURIComponent(originFilter)}` : '/recipes/all';
      backLabel = originFilter || 'Recipes';
    }
  } else {
    backLink = originFilter ? `/recipes/collection/${encodeURIComponent(originFilter)}` : '/recipes/all';
    backLabel = originFilter || 'Recipes';
  }

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Typography variant="body1">Loading recipe...</Typography>
      </Box>
    );
  }
  
  if (!recipe) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Typography variant="body1">Recipe not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      backgroundColor: theme.palette.background.default,
      p: { xs: 1, sm: 3 },
      minHeight: '100%',
      color: theme.palette.text.primary,
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      pb: 10,
      pt: 0,
      px: { xs: 1, sm: 3 }
    }}>
      <DetailHeader 
        title={recipe.recipeNameDisplay}
        actions={
          <RecipeActions
            recipe={recipe}
            favorites={favorites}
            bookmarks={bookmarks}
            toggleFavorite={toggleFavorite}
            toggleBookmark={toggleBookmark}
            onOpenMenuDialog={() => setIsMenuDialogOpen(true)}
            onOpenDeleteDialog={() => setDeleteDialogOpen(true)}
            userRecipe={userRecipe}
          />
        }
        backLink={backLink}
        backLabel={backLabel}
      />
      
      <RecipeServings
        servings={servings}
        setServings={setServings}
        hasEnhancedMeasurements={recipe.hasEnhancedMeasurements}
      />
      
      <Box sx={{ 
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: 1.5, md: 4 },
        mt: { xs: 0, sm: 0.5 },
        position: 'relative',
        zIndex: 1
      }}>
        <Box sx={{ flex: 1, order: { xs: 0, md: 1 } }}>
          <RecipeIngredients 
            recipe={recipe} 
            togglePossession={togglePossession}
          />
          <RecipeInstructions recipe={recipe} />
          
          {/* Add Recipe Notes if user is logged in */}
          {user && (
            <RecipeNotes 
              recipeId={recipeId}
              userId={user.uid}
            />
          )}
        </Box>
        
        <Box sx={{ order: { xs: 1, md: 0 } }}>
          <RecipeImage
            recipe={recipe}
            imageError={imageError}
            setImageError={setImageError}
          />
        </Box>
      </Box>

      <MenuDialog
        open={isMenuDialogOpen}
        onClose={() => setIsMenuDialogOpen(false)}
        recipe={recipe}
        menus={menus}
        onAddRecipeToMenu={onAddRecipeToMenu}
        onOpenCreateMenuDialog={() => setIsCreateMenuDialogOpen(true)}
      />
      
      <CreateMenuDialog
        open={isCreateMenuDialogOpen}
        onClose={() => setIsCreateMenuDialogOpen(false)}
        newMenuName={newMenuName}
        setNewMenuName={setNewMenuName}
        handleCreateAndAddToMenu={handleCreateAndAddToMenu}
        handleKeyPress={handleKeyPress}
      />

      <DeleteRecipeDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        recipe={recipe}
        onDelete={handleDeleteRecipe}
      />
    </Box>
  );
}

export default RecipeDetail; 